import type { APIErrorType } from '@core/types';
import type { ReadBranchCollectionType } from '@readme/api-contracts/schemas/branch';
import type { ReactNode } from 'react';

import { useEffect } from 'react';

import useEnvInfo from '@core/hooks/useEnvInfo';
import useReadmeApi from '@core/hooks/useReadmeApi';

import { useSuperHubStore } from '..';

export interface InitializeSuperHubBranchesProps {
  children: ReactNode;

  /**
   * Initial branches data for this version, typically sourced by SSR props
   * generated by our express controllers.
   */
  initialBranches?: ReadBranchCollectionType;

  /**
   * Whether to revalidate versions data on focus. Default is `false`.
   */
  revalidateOnFocus?: boolean;

  /**
   * The version we are initializing branches data for. When `undefined`,
   * initialization and revalidation are entirely disabled.
   */
  version?: string;
}

/**
 * Initializes the SuperHubStore's version slice with initial data and connects
 * it to the git versions API endpoint to continually keep it in sync.
 */
export function InitializeSuperHubBranches({
  children,
  initialBranches,
  revalidateOnFocus = false,
  version,
}: InitializeSuperHubBranchesProps) {
  const { isServer } = useEnvInfo();
  const [getApiBaseUrl, isEnterpriseGroup, initialize, isReady] = useSuperHubStore(s => [
    s.getApiBaseUrl,
    !!s.enterprise?.isGroup,
    s.branches.initialize,
    s.branches.isReady,
  ]);

  // PART 1: initialize our store during SSR and first CSR render if/when we
  // have initial versions data included in our SSR props generated by our
  // express controller. We must call this inline rather than inside an effect.
  if ((isServer || !isReady) && version && initialBranches) {
    initialize({
      versionsMap: {
        [version]: {
          branches: initialBranches,
          isLoading: false,
          swrKey: null,
        },
      },
    });
  }

  // PART 2: Connect to API via SWR to keep it in sync. This will continually
  // update our store with the API response in an async way. When revalidation
  // is disabled and initial data exists, we can skip the initial connection.
  // Prevent fetching versions for enterprise parent groups also.
  const shouldNotRevalidate = initialBranches && !revalidateOnFocus;
  const shouldNotFetch = !version || shouldNotRevalidate || isEnterpriseGroup;
  const url = shouldNotFetch ? null : `/branches?prefix=v${version}&sort_by=review_status`;
  const {
    data = initialBranches,
    error,
    isLoading,
    swrKey,
  } = useReadmeApi<ReadBranchCollectionType, APIErrorType>(url, {
    apiBaseUrl: getApiBaseUrl({ branch: null }),
    swr: {
      revalidateOnFocus,
      shouldRetryOnError: true,
    },
  });

  useEffect(() => {
    if (!version) return;
    initialize({
      versionsMap: {
        [version]: { branches: data || null, error, isLoading, swrKey },
      },
    });
  }, [data, error, initialize, isEnterpriseGroup, isLoading, swrKey, version]);

  return <>{children}</>;
}
